"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import {
  Sparkles,
  X,
  RotateCcw,
  Send,
  Square,
  ShieldAlert,
} from "lucide-react";
import { useGardenChat } from "@/lib/ai/use-garden-chat";
import { useChatUi, setChatUiOpen } from "@/lib/ai/chat-store";
import { ChatMessage } from "./ChatMessage";
import { ChatActivity } from "./ChatActivity";

const WELCOME_CHIPS_DE = [
  "Was macht Lukas beruflich?",
  "Barrierefreiheit & BITV 2.0",
  "Git Workflows & Rebase",
  "Core Web Vitals & INP",
];

const WELCOME_CHIPS_EN = [
  "What does Lukas do?",
  "Accessibility & WCAG",
  "Git Workflows & Rebase",
  "Core Web Vitals & INP",
];

export default function GardenChatCorner() {
  const pathname = usePathname() || "/";
  const isEn = pathname === "/en" || pathname.startsWith("/en/");
  const { isOpen } = useChatUi();
  const { messages, sendMessage, status, error, stop, setMessages, regenerate } =
    useGardenChat();
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isLoading = status === "streaming" || status === "submitted";

  // Auto-scroll on new messages
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading, isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && isOpen) {
        setChatUiOpen(false);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    sendMessage({ text: input });
    setInput("");
  }

  function handleChipClick(chip: string) {
    sendMessage({ text: chip });
  }

  const chips = isEn ? WELCOME_CHIPS_EN : WELCOME_CHIPS_DE;
  const legalHref = isEn ? "/en/impressum#datenschutz" : "/impressum#datenschutz";

  return (
    <>
      {/* Floating Launcher Button */}
      <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40">
        <button
          type="button"
          onClick={() => setChatUiOpen(!isOpen)}
          aria-label={
            isOpen
              ? isEn
                ? "Close chat"
                : "Chat schließen"
              : isEn
                ? "Ask the garden"
                : "Frag den Garden"
          }
          aria-expanded={isOpen}
          className="group inline-flex items-center gap-2.5 rounded-full border border-black/15 bg-white/95 px-4 py-2.5 text-xs sm:text-sm font-semibold text-neutral-900 shadow-xl backdrop-blur-md transition-all duration-200 hover:scale-[1.03] hover:border-black/30 hover:shadow-2xl dark:border-white/20 dark:bg-neutral-900/95 dark:text-white dark:hover:border-white/40 focus-visible:outline-2 focus-visible:outline-emerald-500"
        >
          {isOpen ? (
            <>
              <X size={17} aria-hidden="true" className="transition-transform group-hover:rotate-90" />
              <span>{isEn ? "Close" : "Schließen"}</span>
            </>
          ) : (
            <>
              <Sparkles size={16} className="text-emerald-500 animate-pulse dark:text-emerald-400" aria-hidden="true" />
              <span>{isEn ? "Ask the garden" : "Frag den Garden"}</span>
            </>
          )}
        </button>
      </div>

      {/* Floating Chat Panel */}
      {isOpen && (
        <aside
          role="dialog"
          aria-label={isEn ? "Garden Assistant" : "Garden Assistent"}
          aria-modal="false"
          className="fixed bottom-20 right-4 sm:right-6 z-40 flex h-[min(620px,calc(100dvh-6.5rem))] w-[calc(100vw-2rem)] sm:w-[420px] flex-col overflow-hidden rounded-3xl border border-black/10 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-white/15 dark:bg-neutral-950/95 animate-in fade-in zoom-in-95 duration-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-black/10 px-5 py-3.5 dark:border-white/10 bg-neutral-50/70 dark:bg-neutral-900/60">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-400">
                <Sparkles size={16} aria-hidden="true" />
              </div>
              <div>
                <h2 className="font-heading text-sm font-bold tracking-tight text-neutral-900 dark:text-white">
                  {isEn ? "Ask the Garden" : "Frag den Garden"}
                </h2>
                <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                  {isEn ? "Portfolio & Notes AI Assistant" : "Assistent zu Projekten & Notizen"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={() => setMessages([])}
                  title={isEn ? "Clear chat" : "Chat leeren"}
                  aria-label={isEn ? "Clear chat" : "Chat leeren"}
                  className="rounded-lg p-1.5 text-neutral-400 hover:bg-black/5 hover:text-neutral-700 dark:hover:bg-white/5 dark:hover:text-neutral-200 transition-colors"
                >
                  <RotateCcw size={15} />
                </button>
              )}
              <button
                type="button"
                onClick={() => setChatUiOpen(false)}
                title={isEn ? "Close" : "Schließen"}
                aria-label={isEn ? "Close chat" : "Chat schließen"}
                className="rounded-lg p-1.5 text-neutral-400 hover:bg-black/5 hover:text-neutral-700 dark:hover:bg-white/5 dark:hover:text-neutral-200 transition-colors"
              >
                <X size={17} />
              </button>
            </div>
          </div>

          {/* Messages Body */}
          <div
            className="flex-1 space-y-3.5 overflow-y-auto px-4 py-4 text-xs sm:text-sm"
            role="log"
            aria-live="polite"
          >
            {messages.length === 0 ? (
              <div className="flex h-full flex-col justify-center space-y-4 py-4 text-center">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-black/5 dark:border-white/10 dark:bg-white/5">
                  <Sparkles className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                </div>
                <div className="space-y-1">
                  <p className="font-semibold text-neutral-900 dark:text-white">
                    {isEn ? "Hi! What would you like to explore?" : "Hi! Was möchtest du wissen?"}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 max-w-xs mx-auto">
                    {isEn
                      ? "I answer questions about Lukas' background, frontend architecture, accessibility, and notes in the garden."
                      : "Ich beantworte Fragen zu Lukas' Projekten, Frontend-Architektur, Barrierefreiheit und Notizen im Garden."}
                  </p>
                </div>

                <div className="pt-2">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-neutral-400 dark:text-neutral-500 mb-2 block">
                    {isEn ? "Suggested Questions" : "Beispielfragen"}
                  </span>
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {chips.map((chip) => (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => handleChipClick(chip)}
                        className="rounded-full border border-black/10 bg-black/[0.03] px-3 py-1.5 text-xs text-neutral-700 transition-all hover:border-black/25 hover:bg-black/5 dark:border-white/10 dark:bg-white/5 dark:text-neutral-300 dark:hover:border-white/20 dark:hover:bg-white/10 text-left"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              messages.map((message) => {
                const isUser = message.role === "user";
                const text = message.parts
                  ?.filter((p) => p.type === "text")
                  .map((p) => ("text" in p ? p.text : ""))
                  .join("");

                if (!text) return null;

                return (
                  <ChatMessage
                    key={message.id}
                    role={isUser ? "user" : "assistant"}
                    text={text}
                  />
                );
              })
            )}

            {isLoading && <ChatActivity locale={isEn ? "en" : "de"} />}

            {error && (
              <div role="alert" className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-xs text-red-600 dark:text-red-400 space-y-1.5">
                <p>
                  {error.message ||
                    (isEn
                      ? "The answer could not be loaded. Please try again."
                      : "Die Antwort konnte nicht geladen werden. Bitte versuche es erneut.")}
                </p>
                <button
                  type="button"
                  onClick={() => regenerate()}
                  className="font-semibold underline underline-offset-2"
                >
                  {isEn ? "Try again" : "Erneut versuchen"}
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Privacy Disclaimer Banner */}
          <div className="border-t border-black/5 bg-neutral-50/50 px-4 py-1.5 text-[11px] text-neutral-500 dark:border-white/5 dark:bg-neutral-900/40 dark:text-neutral-400">
            <div className="flex items-center gap-1.5">
              <ShieldAlert size={12} className="shrink-0 text-amber-500" />
              <span className="truncate">
                {isEn ? "Answers via Gemini. " : "Antworten via Google Gemini. "}
                <strong className="font-medium text-neutral-700 dark:text-neutral-300">
                  {isEn ? "Do not share internal company data." : "Keine internen Firmendaten eingeben."}
                </strong>{" "}
                <Link href={legalHref} className="underline hover:text-neutral-900 dark:hover:text-white">
                  {isEn ? "Privacy info" : "Datenschutz"}
                </Link>
              </span>
            </div>
          </div>

          {/* Input Form */}
          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2 border-t border-black/10 bg-white p-3 dark:border-white/10 dark:bg-neutral-950"
            {...{
              toolname: "ask_digital_garden",
              tooldescription:
                "Fragen an den Assistenten zu Lukas Kopischke und dem Digital Garden",
              toolautosubmit: "true",
            }}
          >
            <input
              ref={inputRef}
              type="text"
              name="query"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isEn ? "Ask a question …" : "Frage stellen …"}
              disabled={isLoading}
              className="flex-1 bg-transparent px-3 py-2 text-xs sm:text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none disabled:opacity-50 dark:text-white dark:placeholder:text-neutral-500"
            />
            {isLoading ? (
              <button
                type="button"
                onClick={() => stop()}
                aria-label={isEn ? "Stop generation" : "Generierung stoppen"}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-neutral-200 text-neutral-700 hover:bg-neutral-300 dark:bg-neutral-800 dark:text-neutral-200 transition-colors"
              >
                <Square size={13} fill="currentColor" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                aria-label={isEn ? "Send message" : "Nachricht senden"}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-30 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200 transition-colors"
              >
                <Send size={14} />
              </button>
            )}
          </form>
        </aside>
      )}
    </>
  );
}
